import React, { createContext, ReactNode, useContext, useMemo } from "react";

import { useEnterprise } from "@/hooks/useEnterprise";
import { usePinnedItems } from "@/hooks/usePinnedItems";
import {
  CreatePinnedItemDto,
  PinnedItem,
  PinnedItemScope,
} from "@/types/pinnedItems";

interface UpdateItemPayload {
  scope?: PinnedItemScope;
}

interface PinnedItemsContextType {
  pinnedItems: PinnedItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  createItem: (dto: CreatePinnedItemDto) => Promise<PinnedItem>;
  updateItem: (itemId: string, updates: UpdateItemPayload) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  reorderItems: (reorderedItems: PinnedItem[]) => Promise<void>;
  refetch: () => void;
  samMessages: PinnedItem[];
  components: PinnedItem[];
  isPinningEnabled: boolean;
  canCreateEnterpriseScope: boolean;
  canDeleteItem: (item: PinnedItem) => boolean;
  canEditScope: boolean;
}

const PinnedItemsContext =
  createContext<PinnedItemsContextType | undefined>(undefined);

export const PinnedItemsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    pinnedItems: rawPinnedItems,
    isLoading,
    isError,
    error,
    refetch,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    isPinningEnabled,
    canCreateEnterpriseScope,
    canDeleteItem,
    canEditScope,
  } = usePinnedItems();

  // Get enterprise state for opt-in checks
  const { states } = useEnterprise();
  const isPeerOptedIn = states?.peerBenchmarkOptIn === true;

  // Filter out components that require opt-in consent (e.g., peer-ranking)
  const pinnedItems = useMemo(() => {
    return rawPinnedItems.filter((item) => {
      // Peer ranking requires opt-in consent
      if (item.componentId === "peer-ranking" && !isPeerOptedIn) {
        return false;
      }
      return true;
    });
  }, [rawPinnedItems, isPeerOptedIn]);

  // Filter items by type
  const samMessages = useMemo(
    () => pinnedItems.filter((item) => item.itemType === "SAM_MESSAGE"),
    [pinnedItems]
  );

  const components = useMemo(
    () => pinnedItems.filter((item) => item.itemType === "COMPONENT"),
    [pinnedItems]
  );

  const contextValue = useMemo(
    () => ({
      pinnedItems,
      isLoading,
      isError,
      error,
      createItem,
      updateItem,
      deleteItem,
      reorderItems,
      refetch,
      samMessages,
      components,
      isPinningEnabled,
      canCreateEnterpriseScope,
      canDeleteItem,
      canEditScope,
    }),
    [
      pinnedItems,
      isLoading,
      isError,
      error,
      createItem,
      updateItem,
      deleteItem,
      reorderItems,
      refetch,
      samMessages,
      components,
      isPinningEnabled,
      canCreateEnterpriseScope,
      canDeleteItem,
      canEditScope,
    ]
  );

  return (
    <PinnedItemsContext.Provider value={contextValue}>
      {children}
    </PinnedItemsContext.Provider>
  );
};

/**
 * Hook to use the PinnedItems context
 */
export const usePinnedItemsContext = (): PinnedItemsContextType => {
  const context = useContext(PinnedItemsContext);
  if (!context) {
    throw new Error(
      "usePinnedItemsContext must be used within a PinnedItemsProvider"
    );
  }
  return context;
};
